<script setup lang="ts">
const runtimeConfig = useRuntimeConfig()

const { data: posts } = await useAsyncData('news', () =>
  queryCollection('news').order('date', 'DESC').all(),
)

const isLocal = runtimeConfig.public.environment === 'local' || runtimeConfig.public.environment === 'offline'

const visiblePosts = computed(() => (posts.value || []).filter(p => !p.draft || isLocal))

const DESCRIPTION = 'Code & Magic の新着情報とエンコーディアのできごとをお届けするニュース。アップデート、みんなの作品、遊び方の話題。'

useSeoMeta({
  title: 'ニュース | Code & Magic',
  description: DESCRIPTION,
  ogTitle: 'ニュース | Code & Magic',
  ogDescription: DESCRIPTION,
  ogImage: `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`,
  twitterCard: 'summary_large_image',
})

useHead({
  link: [
    { rel: 'canonical', href: `${runtimeConfig.public.serviceDomain}/news` },
    { rel: 'alternate', type: 'application/rss+xml', title: 'Code & Magic ニュース RSS', href: '/feed.xml' },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
    { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@400;500;700;800&display=swap' },
  ],
})

definePageMeta({
  layout: 'simple-landing',
})

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
}
</script>

<template>
  <div class="news-bg">
    <div class="centered-area">
      <div class="rounded-area">
        <NuxtLink
          to="/"
          class="home-logo"
        >
          <BrandTextLogo />
        </NuxtLink>

        <div class="news-header">
          <h1 class="news-title">
            ニュース
          </h1>
          <p class="news-subtitle">
            アップデートと、エンコーディアのできごと
          </p>
        </div>

        <div
          v-if="visiblePosts.length > 0"
          class="news-list"
        >
          <NuxtLink
            v-for="post in visiblePosts"
            :key="post.path"
            :to="post.path"
            :class="['news-card', { 'news-card--draft': post.draft }]"
          >
            <img
              v-if="post.image"
              :src="post.image"
              :alt="post.imageAlt || post.title"
              class="news-card-thumb"
              loading="lazy"
            >
            <div class="news-card-body">
              <div class="news-card-meta">
                <time class="news-card-date">{{ formatDate(post.date) }}</time>
                <span
                  v-if="post.category"
                  class="news-card-category"
                >{{ post.category }}</span>
                <span
                  v-if="post.draft"
                  class="draft-badge"
                >非公開</span>
              </div>
              <h2 class="news-card-title">{{ post.title }}</h2>
              <p class="news-card-description">{{ post.description }}</p>
            </div>
          </NuxtLink>
        </div>

        <p
          v-else
          class="news-empty"
        >
          まだ記事がありません。
        </p>

        <div class="news-footer">
          <NuxtLink
            to="/articles"
            class="footer-link"
          >
            遊び方のガイド（エンコーディア通信）
          </NuxtLink>
          <a
            href="/feed.xml"
            class="footer-link"
            target="_blank"
          >
            RSS フィード
          </a>
          <NuxtLink
            to="/"
            class="footer-link"
          >
            ホームに戻る
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '~/assets/css/landing-variables.css';

.news-bg {
  font-family: "Noto Serif JP", "Noto Sans JP", serif;
  color: var(--landing-color-text);
  width: 100%;
  max-width: 100%;
  padding: 0;
}

.centered-area {
  width: 100%;
  max-width: var(--landing-max-width);
  margin: auto;
  padding: var(--landing-gutter);
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.rounded-area {
  margin: 0 0.5em;
  padding: var(--landing-rounded-padding);
  background-color: var(--landing-color-card-warm);
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow);
  position: relative;
  overflow: hidden;
}

.rounded-area::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 160px;
  background-image: url("/images/articles/encodia-world.webp");
  background-size: cover;
  background-position: center top;
  mask-image: linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 60%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.7) 60%, transparent 100%);
  pointer-events: none;
}

/* ---- Home logo ---- */
.home-logo {
  display: block;
  position: relative;
  z-index: 1;
  margin-top: -0.3rem;
  margin-bottom: 0;
}

.home-logo :deep(.shadowed) {
  height: 2.5rem;
  filter: none;
  opacity: 0.5;
  transition: opacity 0.2s;
}

.home-logo:hover :deep(.shadowed) {
  opacity: 0.8;
}

.news-header {
  position: relative;
  padding: 0.5rem 1rem 2rem;
  margin-bottom: 1rem;
}

.news-title {
  color: var(--landing-color-navy);
  font-size: 2rem;
  font-weight: 900;
  text-align: center;
  margin-top: 0;
  margin-bottom: 0.3em;
}

.news-subtitle {
  font-size: 1.05rem;
  text-align: center;
  color: #555;
  margin-bottom: 0;
}

.news-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.news-card {
  display: flex;
  align-items: stretch;
  gap: 0;
  background-color: #fff;
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow-light);
  text-decoration: none;
  color: var(--landing-color-text);
  transition: box-shadow 0.2s, transform 0.2s;
  overflow: hidden;
}

.news-card:hover {
  box-shadow: var(--landing-shadow);
  transform: translateY(-2px);
}

.news-card--draft {
  border: 2px dashed #999;
}

.news-card-thumb {
  flex: 0 0 auto;
  width: 220px;
  aspect-ratio: 1200 / 630;
  object-fit: cover;
  display: block;
  align-self: center;
}

.news-card-body {
  flex: 1 1 auto;
  padding: 1rem 1.25rem;
  min-width: 0;
}

.news-card-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.news-card-date {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--landing-color-teal-dark);
  letter-spacing: 0.04em;
}

.news-card-category {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--landing-color-teal-dark);
  background-color: var(--landing-color-card-mint);
  padding: 0.1rem 0.5rem;
  border-radius: 4px;
}

.draft-badge {
  font-size: 0.7rem;
  font-weight: 700;
  color: #fff;
  background-color: #999;
  padding: 0.1rem 0.5rem;
  border-radius: 4px;
}

.news-card-title {
  font-size: 1.15rem;
  font-weight: 900;
  line-height: 1.5;
  margin-bottom: 0.3rem;
  color: var(--landing-color-navy);
}

.news-card-description {
  font-size: 0.85rem;
  font-weight: 500;
  color: #555;
  line-height: 1.5;
  margin: 0;
}

.news-empty {
  text-align: center;
  color: #555;
  padding: 3rem 1rem;
}

.news-footer {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.footer-link {
  color: var(--landing-color-teal-dark);
  text-decoration: none;
  font-size: 0.85rem;
}

.footer-link:hover {
  text-decoration: underline;
}

@media (max-width: 600px) {
  .news-card {
    flex-direction: column;
  }

  .news-card-thumb {
    width: 100%;
    height: auto;
  }
}
</style>
